import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { CNPJ_DIGITS, Figure, PageHeader, useFormats } from '@/pages/bacen/shared';
import { fetchBacenSettlements } from '@/services/bacen';

const PAGE_SIZE = 50;

/**
 * Termos de Compromisso: the redacted document per settlement and BACEN's
 * own per-year table, shown as a PUBLISHED aggregate beside the measured
 * document counts and never summed with a fine. A settlement is a fence,
 * not a sanction; the per-document amount is refused because the PDFs
 * redact it.
 */
export function BacenSettlementsPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency } = useFormats();
  const [offset, setOffset] = useState(0);
  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['bacen', 'settlements', offset],
    queryFn: () => fetchBacenSettlements({ limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:settlements" exportPath="/api/v1/bacen/export/settlements" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-years>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:settlements.years.title')}
        </h2>
        <Answer answer={data.years} help={t('bacen:settlements.years.help')}>
          {(y) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('bacen:settlements.years.documents')}
                  value={number.format(y.documents)}
                />
                <Figure
                  label={t('bacen:settlements.years.amendments')}
                  value={number.format(y.amendments)}
                />
                <Figure
                  label={t('bacen:settlements.years.nameMatched')}
                  value={number.format(y.name_matched)}
                />
                <Figure
                  label={t('bacen:settlements.years.aps')}
                  value={
                    y.aps_statement
                      ? t(`bacen:settlements.years.aps_${y.aps_statement}`, {
                          defaultValue: y.aps_statement,
                        })
                      : '—'
                  }
                />
              </dl>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('bacen:settlements.years.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:settlements.years.measuredDocuments')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:settlements.years.measuredAmendments')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:settlements.years.publishedCount')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:settlements.years.publishedContribution')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:settlements.years.publishedRestitution')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {y.rows.map((r) => (
                      <tr key={r.year} className="border-t" data-year={r.year}>
                        <td className="px-3 py-2 text-foreground">{r.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(r.documents)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(r.amendments)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {r.published_count === null ? '—' : number.format(r.published_count)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {r.published_contribution === null
                            ? '—'
                            : currency.format(r.published_contribution)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {r.published_restitution === null
                            ? '—'
                            : currency.format(r.published_restitution)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('bacen:settlements.years.published')}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:settlements.guide.years" />
        </div>
      </section>

      <RefusalCard title={t('bacen:settlements.amounts.title')} answer={data.amounts} />

      <section
        className="rounded-lg border bg-card"
        data-documents
        data-stale={isPlaceholderData || undefined}
      >
        <div className="px-4 py-2 text-xs text-muted-foreground">
          <span data-total={data.total_documents}>
            {t('bacen:settlements.documents.count', {
              n: number.format(data.total_documents),
            })}
          </span>
        </div>
        {data.rows.length === 0 ? (
          <div className="p-4">
            <EmptyState hint={t('bacen:settlements.documents.empty')} />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t('bacen:settlements.documents.title')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('bacen:settlements.documents.date')}</th>
                  <th className="px-3 py-2 font-medium">
                    {t('bacen:settlements.documents.entity')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.document_url} className="border-t align-top">
                    <td className="px-3 py-2">
                      <a
                        href={r.document_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {r.title}
                      </a>
                      {r.is_amendment ? (
                        <div className="text-xs text-muted-foreground">
                          {t('bacen:settlements.amendment')}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {r.reference_date ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.entity_cnpj && r.entity_cnpj.length === CNPJ_DIGITS ? (
                        <Link
                          to={`/bacen/e/${r.entity_cnpj}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {r.entity_name ?? r.entity_cnpj}
                        </Link>
                      ) : (
                        <span className="text-xs">{t('bacen:settlements.documents.noMatch')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.total_documents > PAGE_SIZE ? (
          <div className="flex items-center justify-between border-t px-4 py-2 text-xs">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
            >
              {t('bacen:consistency.pairs.previous')}
            </button>
            <span className="text-muted-foreground">
              {t('bacen:consistency.pairs.page', {
                from: number.format(offset + 1),
                to: number.format(Math.min(offset + PAGE_SIZE, data.total_documents)),
              })}
            </span>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= data.total_documents}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
            >
              {t('bacen:consistency.pairs.next')}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
